<script setup lang="ts">
import IconCart from '~icons/mdi/cart-outline'
import IconHeart from '~icons/mdi/heart-outline'
import IconHistory from '~icons/mdi/restore'
import IconLogout from '~icons/mdi/logout'
import IconMenu from '~icons/mdi/menu'
import IconPill from '~icons/mdi/pill'
import IconNeedle from '~icons/mdi/needle'
import IconPetFood from '~icons/mdi/food-apple-outline'
import IconClinic from '~icons/mdi/hospital-building'
import IconClinicChain from '~icons/mdi/paw'
import IconCampaigns from '~icons/mdi/bullhorn-outline'

const constants = useConstants()
const userStore = useUserStore()
const cartStore = useCart()

const localePath = useLocalePath()

const mobileMenuOpen = ref(false)

const categoryMenuItems = useCategoryMenuItems()

const categoryIcons: Record<string, Component> = {
  'Pharma': IconPill,
  'Consumables': IconNeedle,
  'Pet Food': IconPetFood,
}

interface MenuItem { title?: string, to?: string, i18nKey?: string, prependIcon?: Component, class?: string, onClick?: (() => unknown), activeQueryParam?: string, navId?: string }
const menuItems = ref<MenuItem[]>([])

function clickOnMenuItem(item: MenuItem) {
  mobileMenuOpen.value = false
  if (item.onClick)
    item.onClick()
}

watch([() => userStore.data, categoryMenuItems], () => {
  menuItems.value = []

  if (userStore.data?.clinicChain) {
    menuItems.value.push({
      title: userStore.data.clinicChain.name,
      to: localePath('/clinic-chain'),
      prependIcon: IconClinicChain,
      activeQueryParam: constants.QUERY_PARAMS.categories,
      navId: 'mobile_menu_clinic_chain',
    })
  }

  categoryMenuItems.value.forEach(cat => menuItems.value.push({
    title: cat.label,
    to: cat.to,
    prependIcon: categoryIcons[cat.enTerm],
    navId: cat.navId,
  }))

  menuItems.value.push({
    to: localePath('/suppliers'),
    i18nKey: 'layout.menuSecondary.suppliers',
    prependIcon: IconClinic,
    navId: 'mobile_menu_suppliers',
  })

  menuItems.value.push({
    to: localePath('/campaigns'),
    i18nKey: 'layout.menuSecondary.campaigns',
    prependIcon: IconCampaigns,
    navId: 'mobile_menu_campaigns',
  })

  menuItems.value.push({
    i18nKey: 'logout',
    prependIcon: IconLogout,
    onClick: () => { userStore.logout() },
    navId: 'mobile_menu_logout',
  })
}, { immediate: true })
</script>

<template>
  <v-bottom-navigation
    elevation="0"
    grow
    class="w-100 mobile-nav-bar"
  >
    <v-bottom-sheet v-model="mobileMenuOpen">
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          color="red-600"
          data-track-id="mobile_menu_toggle_menu"
          class="pa-0 font-weight-bold"
        >
          <v-icon :icon="IconMenu" size="24" />
          {{ $t('menu') }}
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="(item, index) in menuItems"
          :key="index"
          :title="item.i18nKey ? $t(item.i18nKey) : item.title"
          :to="item.to ? item.to : undefined"
          :class="item.class ? item.class : undefined"
          :data-nav-id="item.navId"
          @click="clickOnMenuItem(item)"
        >
          <template
            v-if="item.prependIcon"
            #prepend
          >
            <v-icon
              size="20"
              class="me-1"
            >
              <component :is="item.prependIcon" />
            </v-icon>
          </template>
        </v-list-item>
      </v-list>
    </v-bottom-sheet>
    <v-btn
      :to="localePath('/order-history')"
      color="red-600"
      class="pa-0 font-weight-bold"
      data-nav-id="nav_order_history"
    >
      <v-icon :icon="IconHistory" size="24" />
      {{ $t('orders') }}
    </v-btn>

    <v-btn
      :to="localePath('/product-favorites')"
      color="red-600"
      class="pa-0 font-weight-bold"
      data-nav-id="nav_favorites"
    >
      <v-icon :icon="IconHeart" size="24" />
      {{ $t('favorites') }}
    </v-btn>
    <v-btn
      v-if="cartStore.totalAmount"
      :to="localePath('/cart')"
      color="red-600"
      class="pa-0 font-weight-bold"
      data-nav-id="bottom_nav_cart"
    >
      <v-badge
        :max="99"
        :content="cartStore.totalAmount"
        offset-x="-5"
        color="black"
      >
        <v-icon :icon="IconCart" size="24" />
      </v-badge>
      {{ $t('cart.title') }}
    </v-btn>
    <v-btn
      v-else
      color="red-600"
      :to="localePath('/cart')"
      data-nav-id="bottom_nav_cart"
      class="font-weight-bold"
    >
      <v-icon :icon="IconCart" size="24" />
      {{ $t('cart.title') }}
    </v-btn>
  </v-bottom-navigation>
</template>

<style scoped lang="scss">
.mobile-nav-bar.v-bottom-navigation {
  background: linear-gradient(90deg, #fbc8db, #fdeef6 30%, #fbc8db) !important;
}

.mobile-nav-bar :deep(.v-bottom-navigation__content .v-btn) {
  padding-top: 8px !important;
  padding-bottom: 0 !important;
  height: 100% !important;
}

.mobile-nav-bar :deep(.v-btn) {
  color: rgb(var(--v-theme-red-600)) !important;
  opacity: 1 !important;
}

.mobile-nav-bar :deep(.v-icon) {
  color: rgb(var(--v-theme-red-600)) !important;
}
</style>
